/**
 * useEnvelopeDraft — manages the full eSign compose wizard state
 * with debounced server sync (1s after last change).
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import * as api from '../lib/esignApi';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function useEnvelopeDraft() {
  const [envelope, setEnvelope] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [syncError, setSyncError] = useState(null);
  const syncTimerRef = useRef(null);

  // Create draft from file upload
  const createDraft = useCallback(async (file, subject) => {
    setLoading(true);
    setError(null);
    try {
      const env = await api.createEnvelope(file, subject);
      setEnvelope(env);
      return env;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounced sync to server.
  // Client-assigned recipient IDs are passed through so fields keep the same
  // recipient_id before and after each replace cycle. Without this, the server
  // generates fresh UUIDs on every call and the field list gets orphaned.
  //
  // Clicking "Add recipient" inserts a blank row whose name+email are empty,
  // which the server rejects with 400 ("Each recipient must have a name").
  // Autosave waits until every recipient is valid — `dirty` stays true so the
  // next keystroke re-triggers the debounce cycle.
  //
  // Only the half that changed is PUT (dragging a field doesn't re-send the
  // recipients), and a failed save is retried with backoff instead of being
  // dropped. Send pushes the draft itself and refuses to go out on a failed
  // save — it used to log the error and send whatever the server had.
  const [syncFailures, setSyncFailures] = useState(0);
  const lastSyncedRef = useRef({ recipients: null, fields: null });
  // Bumped on every edit, so a save that was in flight during an edit
  // doesn't clear `dirty` and strand the newer change.
  const revisionRef = useRef(0);

  const recipientsReady = recipients.every(
    (r) => r.name && r.name.trim() && EMAIL_RE.test(r.email || ''),
  );

  // Throws on failure.
  const pushDraft = useCallback(async () => {
    if (!envelope?.id) return;
    const revision = revisionRef.current;
    const recipientsBody = recipients.map((r, i) => ({
      id: r.id,
      name: r.name,
      email: r.email,
      role: r.role || 'signer',
      order_index: i,
    }));
    const fieldsBody = fields.map(f => ({
      recipient_id: f.recipient_id,
      kind: f.kind,
      required: f.required ?? true,
      page_num: f.page_num,
      x: f.x,
      y: f.y,
      width: f.width,
      height: f.height,
      label: f.label || null,
      default_value: f.default_value || null,
    }));
    const recipientsJson = JSON.stringify(recipientsBody);
    const fieldsJson = JSON.stringify(fieldsBody);
    const last = lastSyncedRef.current;
    if (recipientsBody.length > 0 && recipientsJson !== last.recipients) {
      await api.replaceRecipients(envelope.id, recipientsBody);
      last.recipients = recipientsJson;
    }
    if (fieldsBody.length > 0 && fieldsJson !== last.fields) {
      await api.replaceFields(envelope.id, fieldsBody);
      last.fields = fieldsJson;
    }
    if (revisionRef.current === revision) setDirty(false);
    setSyncFailures(0);
    setSyncError(null);
  }, [envelope?.id, recipients, fields]);

  const syncToServer = useCallback(async () => {
    try {
      await pushDraft();
    } catch (err) {
      console.error('Draft sync failed:', err);
      setSyncError("Couldn't save your draft. Retrying…");
      setSyncFailures((n) => n + 1);
    }
  }, [pushDraft]);

  useEffect(() => {
    if (!dirty || !envelope?.id || !recipientsReady) return;
    clearTimeout(syncTimerRef.current);
    const delay = syncFailures === 0
      ? 1000
      : Math.min(60000, 5000 * 2 ** (syncFailures - 1));
    syncTimerRef.current = setTimeout(syncToServer, delay);
    return () => clearTimeout(syncTimerRef.current);
  }, [dirty, syncToServer, envelope?.id, recipientsReady, syncFailures]);

  const updateMetadata = useCallback(async (updates) => {
    if (!envelope?.id) return;
    try {
      const data = await api.updateEnvelope(envelope.id, updates);
      setEnvelope(prev => ({ ...prev, ...data }));
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [envelope?.id]);

  const updateRecipients = useCallback((newRecipients) => {
    setRecipients(newRecipients);
    revisionRef.current += 1;
    setDirty(true);
  }, []);

  const updateFields = useCallback((newFields) => {
    setFields(newFields);
    revisionRef.current += 1;
    setDirty(true);
  }, []);

  const send = useCallback(async () => {
    if (!envelope?.id) return;
    clearTimeout(syncTimerRef.current);
    if (!recipientsReady) {
      throw new Error('Every recipient needs a name and a valid email address.');
    }
    await pushDraft();
    return api.sendEnvelope(envelope.id);
  }, [envelope?.id, recipientsReady, pushDraft]);

  return {
    envelope, recipients, fields, loading, error, dirty, syncError,
    createDraft, updateMetadata,
    setRecipients: updateRecipients,
    setFields: updateFields,
    send, syncToServer,
  };
}
